
import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { enhanceThemeWithBetterFonts } from "./improvedAdminFonts";

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar Color Palettes (Blue Theme)
// ─────────────────────────────────────────────────────────────────────────────

const FIXED_SIDEBAR_COLORS = Object.freeze({
  // Fixed blue glass sidebar palette.
  // This object is intentionally used for both light and dark mode.
  background: "linear-gradient(135deg, #0078d4 0%, #005a9e 100%)",
  backgroundSolid: "#0078d4",
  border: "rgba(255, 255, 255, 0.18)",
  hover: "rgba(255, 255, 255, 0.12)",
  active: "rgba(255, 255, 255, 0.22)",
  activeStrong: "rgba(255, 255, 255, 0.30)",
  text: "#ffffff",
  textSecondary: "rgba(255, 255, 255, 0.78)",
  icon: "#c7e9ff",
  activeText: "#ffffff",
  activeIcon: "#ffffff",
  divider: "rgba(255, 255, 255, 0.14)",
  glow: "0 10px 35px rgba(0, 90, 158, 0.32)",
});

const SIDEBAR_COLORS = {
  dark: FIXED_SIDEBAR_COLORS,
  light: FIXED_SIDEBAR_COLORS,
};

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const AdminThemeContext = createContext({
  mode:       "dark",
  toggleMode: () => {},
  isDark:     true,
  sidebarColors: SIDEBAR_COLORS.dark,
});

// ─────────────────────────────────────────────────────────────────────────────
// Provider — wrap AdminDashboard with this
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = "sc_admin_theme_mode";

export function AdminThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === "light" ? "light" : "dark";   // default dark
    } catch {
      return "dark";
    }
  });

  const toggleMode = useCallback(() => {
    setMode((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try { localStorage.setItem(STORAGE_KEY, next); } catch (_) {}
      return next;
    });
  }, []);

  // Keep the left sidebar visually fixed when light/dark mode changes.
  // Both dark and light keys intentionally point to the same fixed object.
  const sidebarColors = useMemo(() => SIDEBAR_COLORS[mode] || FIXED_SIDEBAR_COLORS, [mode]);

  // ─────────────────────────────────────────────────────────────────────────
  // Create MUI theme with improved fonts and Windows blue colors
  // ─────────────────────────────────────────────────────────────────────────
  
  const theme = useMemo(() => {
    // Create base theme with Windows blue as primary color
    const baseTheme = createTheme({
      palette: {
        mode,
        primary: {
          main: '#0078d4',      // Windows blue
          light: '#1890ff',     // Lighter blue for hover
          dark: '#005a9e',      // Darker blue for active
          contrastText: '#ffffff',
        },
        secondary: {
          main: '#0078d4',      // Also Windows blue
          light: '#1890ff',
          dark: '#005a9e',
          contrastText: '#ffffff',
        },
      },
      components: {
        // Button hover/active colors
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: 600,
            },
            contained: {
              backgroundColor: '#0078d4',
              '&:hover': {
                backgroundColor: '#1890ff',
              },
              '&:active': {
                backgroundColor: '#005a9e',
              },
            },
            outlined: {
              borderColor: '#0078d4',
              color: '#0078d4',
              '&:hover': {
                borderColor: '#1890ff',
                backgroundColor: 'rgba(0, 120, 212, 0.08)',
              },
            },
          },
        },
        // Tab colors
        MuiTabs: {
          styleOverrides: {
            indicator: {
              height: 3,
              borderRadius: 99,
              backgroundColor: mode === 'dark' ? '#7dd3fc' : '#0369a1',
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 8,
              minHeight: 36,
              color: mode === 'dark' ? '#8294b8' : '#5e7099',
              transition: 'all 0.15s ease',
              '&:hover': {
                color: mode === 'dark' ? '#7dd3fc' : '#0369a1',
                backgroundColor: mode === 'dark'
                  ? 'rgba(56, 189, 248, 0.12)'
                  : 'rgba(3, 105, 161, 0.08)',
              },
              '&.Mui-selected': {
                color: mode === 'dark' ? '#7dd3fc' : '#0369a1',
                backgroundColor: mode === 'dark'
                  ? 'rgba(56, 189, 248, 0.18)'
                  : 'rgba(3, 105, 161, 0.12)',
              },
            },
          },
        },
        // Chip colors
        MuiChip: {
          styleOverrides: {
            root: {
              '&.MuiChip-filled': {
                backgroundColor: '#0078d4',
                color: '#ffffff',
              },
            },
          },
        },
        // IconButton hover
        MuiIconButton: {
          styleOverrides: {
            root: {
              '&:hover': {
                backgroundColor: 'rgba(0, 120, 212, 0.08)',
              },
            },
          },
        },
        // Switch colors
        MuiSwitch: {
          styleOverrides: {
            switchBase: {
              '&.Mui-checked': {
                color: '#0078d4',
                '& + .MuiSwitch-track': {
                  backgroundColor: '#0078d4',
                },
              },
            },
          },
        },
        // Checkbox colors
        MuiCheckbox: {
          styleOverrides: {
            root: {
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              '&.Mui-checked': {
                color: '#0078d4',
              },
            },
          },
        },
        // Radio colors
        MuiRadio: {
          styleOverrides: {
            root: {
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              '&.Mui-checked': {
                color: '#0078d4',
              },
            },
          },
        },
        // Link colors
        MuiLink: {
          styleOverrides: {
            root: {
              color: '#0078d4',
              '&:hover': {
                color: '#1890ff',
              },
            },
          },
        },
      },
    });
    
    // ✅ Enhance with better fonts (medium size + bold small text)
    return enhanceThemeWithBetterFonts(baseTheme);
  }, [mode]);

  return (
    <AdminThemeContext.Provider value={{ 
      mode, 
      toggleMode, 
      isDark: mode === "dark",
      sidebarColors,  // ✅ NEW: Sidebar colors available
    }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AdminThemeContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useAdminTheme() {
  return useContext(AdminThemeContext);
}